import React, { useState } from 'react';
import { Connection } from 'reactflow';
import { EdgeType } from './types';

interface EdgeWizardProps {
  connection: Connection & {
    data?: {
      edgeType: EdgeType;
      sourceKind: string;
      targetKind: string;
    };
  };
  onComplete: (edgeData: any) => void;
  onCancel: () => void;
  saving?: boolean;
  saveError?: string | null;
}

export default function EdgeWizard({ connection, onComplete, onCancel, saving = false, saveError }: EdgeWizardProps) {
  const { edgeType, sourceKind, targetKind } = connection.data || {};
  const [properties, setProperties] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Define required properties based on edge type
  const getRequiredProperties = (type: EdgeType) => {
    switch (type) {
      case 'publishes':
      case 'consumes':
        return [
          { key: 'topic', label: 'Topic Path', type: 'text', required: true, placeholder: 'ingest/dev/collar/raw' },
          { key: 'consumerGroup', label: 'Consumer Group', type: 'text', required: false, placeholder: 'telemetry-processors' }
        ];
      case 'exposes_as':
        return [
          { key: 'path', label: 'API Path', type: 'text', required: true, placeholder: '/api/telemetry/health' },
          { key: 'method', label: 'HTTP Method', type: 'select', required: true, options: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] },
          { key: 'auth', label: 'Authentication', type: 'select', required: true, options: ['JWT', 'API_KEY', 'NONE'] },
          { key: 'rateLimit', label: 'Rate Limit (per minute)', type: 'number', required: false, placeholder: '600' }
        ];
      case 'decodes_with':
        return [
          { key: 'priority', label: 'Priority', type: 'number', required: false, placeholder: '1' },
          { key: 'format', label: 'Data Format', type: 'select', required: false, options: ['protobuf', 'json', 'binary', 'text'] }
        ];
      case 'validates_with':
        return [
          { key: 'strictMode', label: 'Strict Validation', type: 'boolean', required: false },
          { key: 'version', label: 'Template Version', type: 'text', required: false, placeholder: '1.2.0' }
        ];
      case 'stores_in':
      case 'uses':
        return [
          { key: 'path', label: 'Storage Path', type: 'text', required: false, placeholder: '/assets/images/' },
          { key: 'permissions', label: 'Permissions', type: 'select', required: false, options: ['read', 'write', 'admin'] }
        ];
      default:
        return [
          { key: 'description', label: 'Description', type: 'text', required: false, placeholder: 'Describe this relationship' }
        ];
    }
  };

  const requiredProperties = getRequiredProperties(edgeType!);

  const handlePropertyChange = (key: string, value: any) => {
    setProperties(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateAndSubmit = () => {
    const newErrors: Record<string, string> = {};
    
    // Validate required fields
    requiredProperties.forEach(prop => {
      if (prop.required && !properties[prop.key]) {
        newErrors[prop.key] = `${prop.label} is required`;
      }
    });

    // Validate specific field types
    if (properties.method && !['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(properties.method)) {
      newErrors.method = 'Invalid HTTP method';
    }

    if (properties.rateLimit && (isNaN(properties.rateLimit) || properties.rateLimit < 1)) {
      newErrors.rateLimit = 'Rate limit must be a positive number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Auto-fill helpful defaults
    const edgeData = {
      id: `edge:${connection.source}->${connection.target}`,
      type: edgeType,
      from: connection.source,
      to: connection.target,
      props: {
        ...properties,
        // Auto-fill defaults based on edge type
        ...(edgeType === 'exposes_as' && !properties.auth && { auth: 'JWT' }),
        ...(edgeType === 'exposes_as' && !properties.rateLimit && { rateLimit: 600 }),
        ...(edgeType === 'publishes' && !properties.topic && sourceKind === 'Decoder' && {
          topic: `ingest/dev/${connection.source?.split(':').pop()}/raw`
        })
      }
    };

    onComplete(edgeData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Configure Connection</h2>
          
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <div className="text-sm text-gray-600">
              Connecting <strong>{sourceKind}</strong> to <strong>{targetKind}</strong>
            </div>
            <div className="text-sm font-medium text-blue-600 mt-1">
              Relationship: {edgeType?.replace(/_/g, ' ')}
            </div>
          </div>

          <div className="space-y-4">
            {requiredProperties.map(prop => (
              <div key={prop.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {prop.label}
                  {prop.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {prop.type === 'text' && (
                  <input
                    type="text"
                    value={properties[prop.key] || ''}
                    onChange={(e) => handlePropertyChange(prop.key, e.target.value)}
                    placeholder={prop.placeholder}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors[prop.key] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}

                {prop.type === 'number' && (
                  <input
                    type="number"
                    value={properties[prop.key] || ''}
                    onChange={(e) => handlePropertyChange(prop.key, parseInt(e.target.value))}
                    placeholder={prop.placeholder}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors[prop.key] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}

                {prop.type === 'select' && (
                  <select
                    value={properties[prop.key] || ''}
                    onChange={(e) => handlePropertyChange(prop.key, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors[prop.key] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select {prop.label}</option>
                    {prop.options?.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                )}

                {prop.type === 'boolean' && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={properties[prop.key] || false}
                      onChange={(e) => handlePropertyChange(prop.key, e.target.checked)}
                      className="mr-2"
                    />
                    Enable {prop.label}
                  </label>
                )}

                {errors[prop.key] && (
                  <p className="text-red-500 text-xs mt-1">{errors[prop.key]}</p>
                )}
              </div>
            ))}
          </div>

          {saveError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              ⚠️ {saveError}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={validateAndSubmit}
              disabled={saving}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : 'Create Connection'}
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
